import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { getSafeCancelTarget } from '../../utils/authNavigation';
import AuthModal from './AuthModal';
import OtpModal from './OtpModal';
import { usePostHog } from 'posthog-js/react';

/**
 * Unified login flow modal that combines AuthModal and OtpModal
 * Handles the complete authentication flow (OAuth, email OTP)
 */
export default function LoginFlowModal() {
  const { isOpen, closeAuthModal, config } = useAuthModal();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [email, setEmail] = useState('');

  // Close modal when user becomes authenticated and handle redirect/postAuthAction
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      const forceAuth = config?.forceAuth;
      const postAuthAction = config?.postAuthAction;
      
      // If forceAuth is true, we still want to show the modal briefly
      // but execute postAuthAction immediately if user is already authenticated
      // This handles the case where we want to force auth flow even if already logged in
      if (forceAuth && postAuthAction) {
        // For forceAuth, execute immediately (user is already authenticated)
        try {
          postAuthAction();
          closeAuthModal();
          return;
        } catch (error) {
          console.error('Error executing postAuthAction:', error);
        }
      }
      
      // Normal flow: small delay to allow auth state to fully update
      const timer = setTimeout(() => {
        const redirectTo = config?.afterAuthRedirect;
        
        // Execute postAuthAction if provided (e.g., custom navigation logic)
        if (postAuthAction) {
          try {
            postAuthAction();
          } catch (error) {
            console.error('Error executing postAuthAction:', error);
          }
        }
        
        // If we have a redirect target, navigate there after closing modal
        // Only navigate if postAuthAction didn't already handle navigation
        if (redirectTo && !postAuthAction) {
          navigate(redirectTo, { replace: true });
        }
        
        closeAuthModal();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isOpen, closeAuthModal, config, navigate]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowOtpModal(false);
      setEmail('');
    }
  }, [isOpen]);

  // OPTION D: Track unexpected modal opens for debugging
  // This helps identify when/why auth modal opens on pages where users are already authenticated
  useEffect(() => {
    if (isOpen) {
      const currentPath = location.pathname;
      
      // Track if modal opens on /upgrade page while user is authenticated
      // This shouldn't happen and indicates a potential issue
      if (currentPath === '/upgrade' && isAuthenticated) {
        console.warn('[LoginFlowModal] ⚠️ Auth modal opened on /upgrade for authenticated user');
        
        posthog?.capture('unexpected_auth_modal_open', {
          page: currentPath,
          is_authenticated: isAuthenticated,
          config_reason: config?.reason,
          config_from_path: config?.fromPath,
          config_redirect: config?.afterAuthRedirect,
          url_hash: typeof window !== 'undefined' ? window.location.hash : '',
          url_search: typeof window !== 'undefined' ? window.location.search : '',
        });
      }
    }
  }, [isOpen, isAuthenticated, location.pathname, config, posthog]);

  // Don't render if modal is closed
  if (!isOpen) {
    return null;
  }

  // Don't render if already authenticated (unless forceAuth is enabled)
  // If forceAuth is true, we'll show the modal briefly but execute postAuthAction immediately
  const forceAuth = config?.forceAuth;
  if (isAuthenticated && !forceAuth) {
    return null;
  }

  const handleEmailSent = (sentEmail: string) => {
    setEmail(sentEmail);
    setShowOtpModal(true);
  };

  const handleOtpVerified = () => {
    setShowOtpModal(false);
    // Auth state will update via onAuthStateChange, triggering useEffect above
  };

  const handleEditEmail = () => {
    setShowOtpModal(false);
    setEmail('');
  };

  const handleClose = () => {
    setShowOtpModal(false);
    setEmail('');
    
    // Get cancel behavior - default to 'history_or_home_with_gate_check' if not specified
    const cancelBehavior = config?.onCancelBehavior?.type || 'history_or_home_with_gate_check';
    
    // Close modal first
    closeAuthModal();
    
    // Handle cancel based on behavior type
    if (cancelBehavior === 'stay') {
      // For 'stay' type, just close the modal and don't navigate anywhere
      // The page state will remain intact
      return;
    }
    
    // Default behavior: 'history_or_home_with_gate_check'
    // Determine safe cancel target
    // Use fromPath from config if available (path where modal was opened), otherwise use current pathname
    const fromPath = config?.fromPath || location.pathname;
    const safeTarget = getSafeCancelTarget({ pathname: fromPath }, isAuthenticated);
    
    // Navigate to safe target
    if (safeTarget === '/') {
      // If safe target is home, navigate there (fromPath was protected)
      navigate('/', { replace: true });
    } else {
      // If safe target is a non-protected route, try navigate(-1) to preserve browser history
      // getSafeCancelTarget already ensures safeTarget is not protected, so navigate(-1) is safe
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(safeTarget, { replace: true });
      }
    }
  };

  if (showOtpModal) {
    return (
      <OtpModal
        email={email}
        onClose={handleClose}
        onEditEmail={handleEditEmail}
        onVerified={handleOtpVerified}
      />
    );
  }

  // Hide sign-in toggle and cancel button when modal is opened from /lander-1
  const hideSignInToggleAndCancel = config?.fromPath === '/lander-1';

  return (
    <AuthModal
      onClose={handleClose}
      onEmailSent={handleEmailSent}
      actionName="continue"
      hideSignInToggleAndCancel={hideSignInToggleAndCancel}
    />
  );
}

